import React, { useRef, useState } from 'react';

import i18n from '../../i18n';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import styles from './MyList.module.scss';

import Icon from '../Icon';
import MyListEntry from '../MyListEntry';
import GuideList from '../GuideList';
import TabbedLists from '../TabbedLists';
import Button from '../Button';
import Modal from '../Modal';
import { generateUrl } from '../../utils/MyListStorage';
import ButtonRound from '../ButtonRound';
import {
    hasNativeShareSupport,
    triggerNativeShareDialog,
} from '../../utils/nativeShare';

const MyListEmpty = ({ title }) => {
    return (
        <section className={styles['MyListEmpty']}>
            <div className={styles['MyListEmpty__Grid']}>
                <div className={styles['MyListEmpty__IconWrapper']}>
                    <Icon type="bookmark" color="black800" size="xl" />
                </div>

                <p className={styles['MyListEmpty__Text']}>
                    {i18n.t('MyList.empty')}
                </p>
            </div>

            <article className={styles['MyListEmpty__GuideListWrapper']}>
                <GuideList title={title} isEmptyMyList />
            </article>
        </section>
    );
};

const MyList = ({
    title,
    filters,
    relatedItems,
    hasItems = false,
    showRemoveButtons = true,
    tabs = [],
    items = {},
}) => {
    const [showModal, setShowModal] = useState(false);
    const [shareLink, setShareLink] = useState('');

    const handleOpenModal = () => {
        const shareUrl = generateUrl(window.location);
        if (hasNativeShareSupport()) {
            return triggerNativeShareDialog(
                i18n.t('MyList.shareText'),
                shareUrl
            );
        }
        document.body.style.overflow = 'hidden';
        setShowModal(true);
        setShareLink(shareUrl);
    };

    const buttonRef = useRef(null);
    const handleCloseModal = () => {
        document.body.style.overflow = 'visible';
        if (buttonRef && !!showModal) {
            buttonRef.current.focus();
        }
        setShowModal(false);
    };

    const getShareLinkSM = () => {
        const encodedUrl = encodeURIComponent(shareLink);
        return {
            facebook: {
                title: title,
                text: i18n.t('MyList.shareText'),
                url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&amp;src=sdkpreparse`,
            },
            mail: `mailto: ?subject=Visit Stockholm&body=${
                i18n.t('MyList.shareWithMail') + shareLink
            }`,
        };
    };

    const shareLinkFb = () => {
        window.open(getShareLinkSM().facebook.url);
    };

    const copyShareLinkToClipboard = () => {
        try {
            if (navigator.clipboard != undefined) {
                //Chrome
                navigator.clipboard.writeText(shareLink);
            } else if (window.clipboardData) {
                // Internet Explorer
                window.clipboardData.setData('Text', shareLink);
            }
        } catch (e) {}
    };

    return (
        <section className={styles['MyList']}>
            <div
                className={classNames(styles['MyList__EntryWrapper'], {
                    [styles['MyList__EntryWrapper--List']]: hasItems,
                })}>
                <MyListEntry
                    showButton={hasItems}
                    ref={buttonRef}
                    handleOpenModal={handleOpenModal}
                />
            </div>

            {showModal && (
                <Modal
                    handleCloseModal={handleCloseModal}
                    ariaLabelledby="modalTitle"
                    ariaDescribedby="modalDescription">
                    <div className={styles['MyList__Modal']}>
                        <button
                            autoFocus
                            aria-label={i18n.t('Modal.close')}
                            label={i18n.t('Modal.close')}
                            className={styles['MyList__ModalCloseButton']}
                            onClick={handleCloseModal}>
                            <Icon type="close" color="black800" size="large" />
                        </button>

                        <strong
                            id="modalTitle"
                            className={styles['MyList__ModalTitle']}>
                            {i18n.t('Modal.modalTitle')}
                        </strong>

                        <div className={styles['MyList__ModalButtonsWrapper']}>
                            <ButtonRound
                                type="facebook"
                                modalSize={true}
                                onClick={shareLinkFb}
                            />
                            <ButtonRound
                                type="mail"
                                modalSize={true}
                                href={getShareLinkSM().mail}
                            />
                        </div>

                        <input
                            className={styles['MyList__ModalInput']}
                            type="text"
                            readOnly
                            value={shareLink}
                        />

                        <Button
                            type="secondary"
                            text={i18n.t('generic.copyLink')}
                            onClick={copyShareLinkToClipboard}
                            iconColor="black800"
                        />
                        <span id="modalDescription" className="sr-only">
                            {i18n.t('MyList.modalDescription')}
                        </span>
                    </div>
                </Modal>
            )}

            <div className={styles['MyList__Main']}>
                {hasItems ? (
                    <TabbedLists
                        showRemoveButtons={showRemoveButtons}
                        defaultTab={0}
                        tabs={tabs}
                        items={items}
                        isMyList
                    />
                ) : (
                    <MyListEmpty
                        title={title}
                        filters={filters}
                        relatedItems={relatedItems}
                    />
                )}
            </div>
        </section>
    );
};

MyList.propTypes = {
    title: PropTypes.string,
    filters: PropTypes.array,
    relatedItems: PropTypes.array,
    hasItems: PropTypes.bool,
    tabs: PropTypes.array,
    items: PropTypes.object,
};

export default MyList;
